import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Clock, Flame, ChefHat, Utensils, Gauge, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChefId } from '@/types/chef';
import { getChefVisualTheme } from '@/config/chefVisualThemes';

interface RecipeTeaserProps {
  query: string;
  complexity: number;
  dietaryTags: string[];
  chefId: ChefId | string;
  className?: string;
}

const complexityTimeMap: Record<number, { min: number; max: number; label: string }> = {
  1: { min: 15, max: 30, label: 'Quick' },
  2: { min: 30, max: 60, label: 'Moderate' },
  3: { min: 60, max: 120, label: 'Involved' }
};

const complexitySkillMap: Record<number, { level: string; description: string }> = {
  1: { level: 'Easy', description: 'Simple techniques, beginner-friendly' },
  2: { level: 'Intermediate', description: 'Some technique required' },
  3: { level: 'Advanced', description: 'Multiple techniques, precision needed' }
};

const dietaryImpacts: Record<string, string> = {
  LowCarb: 'Reduced carbohydrates',
  HealthyLight: 'Lighter, nutrient-focused',
  GlucoseFriendly: 'Blood sugar optimized',
  Vegetarian: 'No meat or fish',
  Vegan: 'Plant-based only',
  GlutenFree: 'No wheat, barley, rye',
  DairyFree: 'No dairy products'
};

const RecipeTeaser: React.FC<RecipeTeaserProps> = ({
  query: _query,
  complexity,
  dietaryTags,
  chefId,
  className = ''
}) => {
  const chefTheme = getChefVisualTheme(chefId);
  const timeInfo = complexityTimeMap[complexity] || complexityTimeMap[2];
  const skillInfo = complexitySkillMap[complexity] || complexitySkillMap[2];

  const estimatedCalories = useMemo(() => {
    const baseCal = 400;
    if (dietaryTags.includes('HealthyLight')) return baseCal - 100;
    if (dietaryTags.includes('LowCarb')) return baseCal - 50;
    return baseCal;
  }, [dietaryTags]);

  const equipmentPreview = useMemo(() => {
    if (complexity === 1) return ['Pan', 'Basic tools'];
    if (complexity === 2) return ['Pan', 'Oven', 'Blender'];
    return ['Multiple pans', 'Oven', 'Thermometer', 'Stand mixer'];
  }, [complexity]);

  const techniqueHint = useMemo(() => {
    const techniques = chefTheme.signatureStyles;
    return techniques[Math.floor(Math.random() * techniques.length)];
  }, [chefTheme]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={cn(
        "relative overflow-hidden rounded-2xl p-4",
        "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900",
        "border border-gray-200 dark:border-gray-700",
        "shadow-lg",
        className
      )}
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-pink-500/5 rounded-full blur-2xl" />
      
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className={cn("w-4 h-4", chefTheme.accentColorLight)} />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Recipe Preview
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-gray-800/60 rounded-lg">
            <Clock className="w-4 h-4 text-blue-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Time</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {timeInfo.min}-{timeInfo.max} min
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-gray-800/60 rounded-lg">
            <Gauge className="w-4 h-4 text-purple-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Difficulty</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {skillInfo.level}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-gray-800/60 rounded-lg">
            <Flame className="w-4 h-4 text-orange-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Est. Calories</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                ~{estimatedCalories} per serving
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-2 bg-white/60 dark:bg-gray-800/60 rounded-lg">
            <Utensils className="w-4 h-4 text-green-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Equipment</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                {equipmentPreview.slice(0, 2).join(', ')}
              </p>
            </div>
          </div>
        </div>

        {dietaryTags.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Dietary Impact</p>
            <div className="flex flex-wrap gap-1.5">
              {dietaryTags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full"
                >
                  {dietaryImpacts[tag] || tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={cn(
          "p-3 rounded-lg",
          `bg-gradient-to-r ${chefTheme.cardGradient}`,
          "border border-gray-200/50 dark:border-gray-700/50"
        )}>
          <div className="flex items-center gap-2 mb-1">
            <ChefHat className={cn("w-4 h-4", chefTheme.accentColor)} />
            <span className={cn("text-xs font-medium", chefTheme.accentColor)}>
              Chef's Approach
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {techniqueHint} style • {skillInfo.description.toLowerCase()}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default RecipeTeaser;
